/**
 * Hook to resolve the default app for the current workspace.
 * Checks localStorage first, falls back to fetching apps and picking the first one.
 */

import { useState, useEffect } from 'react'
import { fetchApps } from '@/services/app-service'
import type { HttpError } from '@/types'

const STORAGE_KEY = 'taqat_default_app_id'

/**
 * Narrow an unknown thrown value to our HttpError shape without importing the class.
 * Auth errors are already handled by api-client (tokens cleared, redirect to /signin
 * scheduled via window.location.href), so the hook must stay silent for those —
 * otherwise the "No apps yet" screen flashes right before the redirect.
 */
function isAuthHttpError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const httpError = error as Partial<HttpError>
  return httpError.type === 'authentication' || httpError.status === 401
}

/**
 * Gets the stored default app ID from localStorage
 */
function getStoredDefaultAppId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * Saves the default app ID to localStorage
 */
export function setDefaultAppId(appId: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, appId)
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Clears the stored default app ID
 */

/**
 * Hook that resolves the default app for the current workspace.
 * Returns `hasNoApps=true` only when the workspace genuinely has zero apps —
 * network/server errors are surfaced via `error` so callers can show a real
 * failure state instead of the misleading empty-workspace UI.
 */
export function useDefaultApp() {
  const [appId, setAppId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasNoApps, setHasNoApps] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function resolveDefaultApp() {
      setIsLoading(true)
      setError(null)
      try {
        const apps = await fetchApps()

        if (cancelled) return

        if (apps.length === 0) {
          setHasNoApps(true)
          setIsLoading(false)
          return
        }

        const storedId = getStoredDefaultAppId()
        const storedAppExists = storedId && apps.some((app) => app.id === storedId)

        if (storedAppExists) {
          setAppId(storedId)
        } else {
          const firstApp = apps[0]
          setDefaultAppId(firstApp.id)
          setAppId(firstApp.id)
        }
      } catch (err) {
        if (cancelled) return

        // api-client has already scheduled a redirect for auth failures — stay
        // silent so the user sees the sign-in page, not an "empty apps" flash.
        if (isAuthHttpError(err)) {
          return
        }

        // Genuine backend failure — surface it as an error, not as "no apps yet".
        setError(err instanceof Error ? err.message : 'Failed to load apps')
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    resolveDefaultApp()

    return () => {
      cancelled = true
    }
  }, [])

  return { appId, isLoading, hasNoApps, error }
}
